// new with selection moved upwards

/*  src/App.js  */
import React, { useState, useContext, useRef, useEffect } from 'react';
import {
  CssBaseline, Box, TextField, IconButton, Typography, Button, Avatar,
  AppBar, Toolbar, Paper, Snackbar, Alert, Badge, Tooltip, Drawer,
  List, ListItem, ListItemText, ListItemIcon, ListItemButton,
  useMediaQuery, Dialog, DialogTitle, DialogContent, DialogActions,
  Modal
} from '@mui/material';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { v4 as uuidv4 } from 'uuid';
import axios from 'axios';
import iQuestLogo from '../pages/images/iQuestLogo.png';
import product_questionaire from '../data/product_questionaire.json'
/* ─── icons ────────────────────────────────────────────── */
import SendIcon from '@mui/icons-material/Send';
import ThumbUpSharp from '@mui/icons-material/ThumbUpSharp'
import ThumbDownAltOutlined from '@mui/icons-material/ThumbDownAltOutlined'
import ThumbUpAltOutlined from '@mui/icons-material/ThumbUpAltOutlined'

import SmartToyIcon from '@mui/icons-material/SmartToy';
import PublicIcon from '@mui/icons-material/Public';
import GroupsIcon from '@mui/icons-material/Groups';
import SecurityIcon from '@mui/icons-material/Security';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MenuIcon from '@mui/icons-material/Menu';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import FeedbackIcon from '@mui/icons-material/Feedback';
import ThumbUpOffAltIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownIcon from '@mui/icons-material/ThumbDownAltOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ReplayIcon from '@mui/icons-material/Replay';
import CloseIcon from '@mui/icons-material/Close';

import apiClient from '../utils/apiClient';
import { UserContext } from '../context/UserContext';
// import LoginPage from './pages/LoginPage';
/* ─── sample data ──────────────────────────────────────── */
import chatbotMapping from '../data/chatbot_product_card_con.json';
import productFrontend from '../data/product_frontend.json';
import { useNavigate } from 'react-router-dom';


/* ─── brand theme (unchanged) ──────────────────────────── */
const theme = createTheme({
  palette: {
    primary: { main: 'rgb(112 48 160)', light: '#3371AC', dark: '#003366', contrastText: '#fff' },
    secondary: { main: '#FF7519', light: '#FF9649', dark: '#D15700', contrastText: '#fff' },
    background: { default: '#f5f7f9', paper: '#fff' }
  },
  typography: { fontFamily: '"Montserrat","Roboto","Helvetica","Arial",sans-serif' },
  shape: { borderRadius: 8 },
  components: { MuiButton: { styleOverrides: { root: { textTransform: 'none', fontWeight: 600 } } } }
});
/* ─── global scrollbars (unchanged) ───────────────────── */
const GlobalStyles = () => (
  <style>{`
    ::-webkit-scrollbar{width:6px;height:6px}
    ::-webkit-scrollbar-track{background:transparent}
    ::-webkit-scrollbar-thumb{background:rgba(0,0,0,.15);border-radius:4px}
    ::-webkit-scrollbar-thumb:hover{background:rgba(0,0,0,.3)}
    body,#root{margin:0;height:100vh;overflow:hidden}
  `}</style>
);
/* ─ meta for each assistant ─ */
const botMeta = {
  product: { icon: <SmartToyIcon fontSize="small" />, color: theme.palette.primary.main, label: 'Product Assistant' },
  nri: { icon: <PublicIcon fontSize="small" />, color: '#2E7D32', label: 'NRI Assistant' },
  lifeai: { icon: <GroupsIcon fontSize="small" />, color: '#9C27B0', label: 'LIFE-AI Team' },
  uw: { icon: <SecurityIcon fontSize="small" />, color: '#D32F2F', label: 'Underwriter Assistant' }
};
/* ─ NRI categories ─ */
const nriCategories = ["GST",
  "Login process for EU and UK",
  "Login process",
  "UW guidelines for savings and protection",
  "TDS",
  "Std countries - investment plans",
  "NRI FAQs",
  "DAPP Approval"];

const categoryMapping = {
  GPP: 'Retirement',
  'Non-Linked Waiver of premium': 'Riders',
  'iPS Plus': 'Protection & Health',
  Signature: 'Market-linked',
  'GIFT Select': 'Guaranteed',
  'Assured Savings Insurance Plan': 'Guaranteed',
  GIFT: 'Guaranteed',
  'GIFT (Long-term)': 'Guaranteed',
  'GIFT Pro': 'Guaranteed',
  'Gold': 'Part-guaranteed',
  'Sukh Samruddhi': 'Part-guaranteed',
  'Savings Suraksha': 'Part-guaranteed',
  'Future Perfect': 'Part-guaranteed',
  'Platinum': 'Market-linked',
  'LifeTime classic': 'Market-linked',
  'Pru1Wealth': 'Market-linked',
  'Smart Goal Assure': 'Market-linked',
  'Signature Assure': 'Market-linked',
  'Protect N Gain Growth': 'Market-linked',
  'Protect N Gain Life': 'Protection with market-linked returns',
  'iPS ROP': 'Protection & Health',
  'iProtect Super': 'Protection & Health',
  'iProtect Supreme': 'Protection & Health',
  'iProtect Supreme ROP': 'Protection & Health',
  'Saral Jeevan Bima': 'Protection & Health',
  'WISH': 'Protection & Health',
  'GPP flexi': 'Retirement',
  'Saral pension': 'Retirement',
  'Signature pension': 'Retirement',
  'Gold pension savings': 'Retirement',
  'Non-Linked Accidental death and disability': 'Riders',
  'Linked Waiver of premium': 'Riders',
  'Linked Accidental death and disability': 'Riders',
  'Non-Linked Goal Protect': 'Riders'
};
const getCategoryForProduct = (prod) => categoryMapping[prod] || 'General';

const optionClickedPostProcessor = (e) => {
  console.log(e.target)
  Array.from(e.target.parentElement.children).forEach((children) => { children.style.border = '1px soild lightgray'; children.style.color = 'gray'; children.disabled = true });
  e.target.disabled = true;
  e.target.style.backgroundColor = '#7030a0';
  e.target.style.color = 'white'
}


  /* ─ tiny presentational bits ─ */
  const Bubble = ({ index, m, bot, options, onOptionClick, length_vec, posetiveFeedBackIndex, submitFeedbackPosetive, negativeFeedBackIndex, openFeedbackNegative }) => {
    const user = m.role === 'user';
    return (
      <Box sx={{ display: 'flex', justifyContent: user ? 'flex-end' : 'flex-start', mb: 2 }}>
        {!user && (
          <Avatar sx={{ bgcolor: botMeta[m.botType]?.color || 'primary.main', mr: 1, width: 36, height: 36 }}>
            {botMeta[m.botType]?.icon}
          </Avatar>
        )}
        <Paper
          sx={{
            overflowY:'scroll',
            p: 1.5, maxWidth: { xs: '85%', sm: '70%' }, borderRadius: 3,
            bgcolor: user ? 'primary.main' : '#f7edff ',
            color: user ? 'primary.contrastText' : 'text.primary',
            borderTopLeftRadius: user ? 30 : 0, borderTopRightRadius: user ? 0 : 30,
            '& a': { color: user ? 'inherit' : 'primary.main' }
          }}
        >

          {m.text == '...' && (
            <span remarkPlugins={[remarkGfm]}>Typing ... </span>
          )}

          {m.text != '...' && (
            <span sx={{ fontSize: '10x' }} >
              {bot!='product'? (<ReactMarkdown components={{ a: ({node, children}) => <span>{children}</span>}}>{m.text}</ReactMarkdown>):(<ReactMarkdown>{m.text}</ReactMarkdown>)}
            </span>
          )}

          {/* Show options inside the bubble if provided */}
          {!user && options && options.length > 0 && (
            <Box sx={{ mt: 1.5, display: 'flex',  flexWrap: 'wrap' }}>
              {options.map(o => (
                <Button
                  key={o.text}
                  variant="outlined"
                  sx={{ mr: 1, mb: 1, borderRadius: 4, fontSize: '10px' }}
                  startIcon={o.icon}
                  onClick={(e) => { onOptionClick(o.text); optionClickedPostProcessor(e) }}
                >
                  {o.text}
                </Button>
              ))}
            </Box>
          )}

          {!user && m.botType && (

            <Typography sx={{ mt: 1, display: 'block', color: 'text.secondary', fontSize: '0.7rem' }}>
              {/* {botMeta[m.botType].label} */}
            </Typography>
          )}
        </Paper>
        {(!user) && (index == length_vec - 1) && (index > 2) ? (<>
          <button style={{ backgroundColor: "#fff", border: 'none' }} >
            {posetiveFeedBackIndex == index  ? (
              <button style={{ backgroundColor: "#7030a0", cursor: 'pointer', border: '2px solid #7030a0', borderRadius: '50%', padding: '8px 9px 8px 9px' }}>
                <ThumbUpOffAltIcon sx={{ color: '#fff' }} />
              </button>

            ) : (
              <button style={{ backgroundColor: "#fff", cursor: 'pointer', border: '2px solid #7030a0', borderRadius: '50%', padding: '8px 9px 8px 9px' }} onClick={() => { submitFeedbackPosetive(index); }}>
                <ThumbUpOffAltIcon sx={{ color: '#7030a0' }} />
              </button>


            )}
          </button>
          <button style={{ backgroundColor: "#fff", border: 'none' }} >
            {negativeFeedBackIndex == index ? (

              <button style={{ backgroundColor: "#7030a0", cursor: 'pointer', border: '2px solid #7030a0', borderRadius: '50%', padding: '8px 9px 8px 9px' }}>
                <ThumbDownAltOutlined sx={{ color: '#fff' }} />
              </button>
            ) : (
              <button style={{ backgroundColor: "#fff", cursor: 'pointer', border: '2px solid #7030a0', borderRadius: '50%', padding: '8px 9px 8px 9px' }} onClick={() => { openFeedbackNegative(index); }}>
                <ThumbDownAltOutlined sx={{ color: '#7030a0' }} />
              </button>
            )}
          </button>
        </>) : (<></>)}

      </Box>
    );
  };
  /* ─────────────────────────────────────────────────────── */


export default function App2() {
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const bottomPad = isMobile ? 72 : 0;
  const endRef = useRef(null);

  /* greet only once per session */
  const greeted = useRef(false);
  /* NEW – bump this number to force a fresh greeting */
  const [resetCount, setResetCount] = useState(0);
  /* ui */
  const [drawer, setDrawer] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', sev: 'info' });
  const [dialog, setDialog] = useState({ open: false, type: '' });

  /* chat */
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [typing, setTyping] = useState(false);
  /* active assistant */
  const [bot, setBot] = useState(null);

  /* product extras */
  const [prodSession, setProdSession] = useState('');
  const [selProd, setSelProd] = useState('');
  const [prodDetails, setProdDetails] = useState(null);
  const [citations, setCitations] = useState([]);
  /* NRI extras */
  const [selNriCategory, setSelNriCategory] = useState('');
  /* the other IDs remain unchanged */
  const [nriSession] = useState(uuidv4());
  // const { setUserNTID, setUserChannel, userNTID } = useContext(UserContext);

  let [product_category_state, set_product_category_state] = useState(0)

  const [lifeSession, setLifeSession] = useState(null);
  const [feedback_open, setFeedback_open] = useState(0)
  const [lifeSessionRef, setLifeSessionRef] = useState('');
  const [starterTokenRef, setStarterTokenRef] = useState('');
  const [introLoading, setIntroLoading] = useState(0)
  // Underwritter tokens 
  const [uwToken] = useState(uuidv4());
  const [uwSession, setUwSession] = useState(() => uuidv4());
  const uwSessionRef = useRef(uwSession);
    const navigate = useNavigate();
      const userNTID = localStorage.getItem('user_ntid')

  // const [uwSession,setUwSession]=useState('');
  const pickSessionID = (d) => d.sessionid || d.sessionId || d.lifeSession || null;
  /* ─── utilities ─────────────────────────────────────── */
  const pushUser = t => setMessages(m => [...m, { role: 'user', text: t }]);
  const pushBot = (t, b = null, options = null) => setMessages(m => [...m, { role: 'assistant', text: t, botType: b, options }]);
  const notice = (msg, sev = 'info') => setSnack({ open: true, msg, sev });
  const FeedbackPopupClose = () => {
    setFeedback_open(0)
  }
  const scrollDown = () => endRef.current?.scrollIntoView({ behavior: 'smooth' });
  useEffect(scrollDown, [messages]);
  /* ─── GREETING (run once per reset) ─────────────────── */
  useEffect(() => {
    if (greeted.current) return;
    greeted.current = true;
    /* Welcome message with options data attached */
    setIntroLoading(1)
    getUsername().then((resp) => {
      setMessages([{
        role: 'assistant',
        text: "👋 Hi " + resp + ", Welcome to ICICI Prudential's Unified Assistant! How can I help you today?\n\nPlease choose one of the services below to get started:",
        botType: null,
        options: [
          { text: "I have questions about products", icon: <SmartToyIcon /> },
          { text: "I have questions about NRIs", icon: <PublicIcon /> },
          { text: "I have a question about Login and Claim process", icon: <GroupsIcon /> },
          // { text: "I need underwriting help", icon: <SecurityIcon /> }
        ]
      }])
      setIntroLoading(0)

    }).catch((error) => {
      setMessages([{
        role: 'assistant',
        text: "👋 Hi User, Welcome to ICICI Prudential's Unified Assistant! How can I help you today?\n\nPlease choose one of the services below to get started:",
        botType: null,
        options: [
          { text: "I have questions about products", icon: <SmartToyIcon /> },
          { text: "I have questions about NRIs", icon: <PublicIcon /> },
          { text: "I have a question about Login and Claim process", icon: <GroupsIcon /> },
          { text: "I need underwriting help", icon: <SecurityIcon /> }
        ]
      }]);
      setIntroLoading(0)



    })
  }, [uwToken, resetCount]);
  const [posetiveFeedBackIndex, setPosetiveFeedBackIndex] = useState(-9999)
  const [negativeFeedBackIndex, setNegativeFeedBackIndex] = useState(-9999)
  // prevent back button leaving chat (keeps behaviour you had)
useEffect(() => {
   const handlePopState = () => window.history.pushState(null, "", window.location.href);
   window.history.pushState(null, "", window.location.href);
   window.addEventListener("popstate", handlePopState);
   return () => window.removeEventListener("popstate", handlePopState);
}, []);

  /* ─── OPTION handler ───────────────────────────────── */
  const handleOption = async txt => {
    pushUser(txt);
    setTyping(true);
    /* first-level selection */
    if (["I have questions about products", "I have questions about NRIs",
      "I have a question about Login and Claim process", "I need underwriting help"].includes(txt)) {
      const map = {
        "I have questions about products": 'product',
        "I have questions about NRIs": 'nri',
        "I have a question about Login and Claim process": 'lifeai',
        "I need underwriting help": 'uw'
      };
      resetSelective()
      const chosen = map[txt];
      setBot(chosen);
      /* for product bot: list all products */
      if (chosen === 'product') {

        let categories = ['Retirement', 'Riders', 'Protection & Health', 'Market-linked', 'Guaranteed', 'Part-guaranteed', 'Protection with market-linked returns']
        pushBot("Here are the categories products we offer. Pick one:", 'product_list',
          categories.map(p => ({ text: p, icon: <SmartToyIcon /> })));

      }
      /* for NRI bot: show category options */
      else if (chosen === 'nri') {
        pushBot("Please select a category for your NRI query:", 'nri',
          nriCategories.map(cat => ({ text: cat, icon: <PublicIcon /> })));
      }
      else {
        pushBot("Great — how can I assist you?", chosen);
      }
      setTyping(false);
      return;
    }
    /* product selection */
    if (bot === 'product' && !prodSession) {
      if (product_category_state == 0) {
        try {
          // const {data}=await axios.get('http://localhost:8000/product-chatbot/chatbot/products');
          const { data } = await apiClient.post('/biu-chatbot/products', { ntID: userNTID });
          let products = data.products || [];
          products = products.filter((e) => {
            return categoryMapping[e] == txt
          })

          if (products.length) {
            pushBot("Here are the products we offer. Pick one:", 'product',
              products.map(p => ({ text: p, icon: <SmartToyIcon /> })));
            set_product_category_state(1)
          } else pushBot("I couldn't fetch the product list.", "product");
        } catch (e) {
          console.error(e); pushBot("Failed to fetch products.", "product");

        }
      } else {
        await startProd(txt);

      }


      setTyping(false); return;
    }
    /* NRI category selection */
    if (bot === 'nri' && !selNriCategory && nriCategories.includes(txt)) {
      setSelNriCategory(txt);
      pushBot(`I've selected the **${txt}** category. How can I help you with your NRI query?`, 'nri');
      setTyping(false); return;
    }
    await handleSend(txt, false);
  };

  const Logout = ()=>{
    localStorage.clear()
    navigate('/');

  }

  /* ─── product helpers ───────────────────────────────── */
  const startProd = async p => {
    try {
      // const {data}=await axios.post('http://localhost:8000/product-chatbot/chatbot/session',{product:p});
      const { data } = await apiClient.post('/biu-chatbot/session', { ntID: userNTID, product: p });
      setProdSession(data.session_id);
      setSelProd(p);
      await loadProdDetails(p);
      let questions = []
      if (Object.keys(product_questionaire).includes(p)) {
        questions = product_questionaire[p].map((i) => { return { text: i, icon: <ArrowForwardIcon /> } })
      }
      pushBot(`I've selected **${p}**. What would you like to know?`, 'product', questions);
    } catch (e) { console.error(e); pushBot("Sorry — It's not you, its us. Please try again after sometime.", "product"); }
  };
  const loadProdDetails = async p => {
    try {
      const map = chatbotMapping.find(i => i.ChatbotProductName === p);
      if (!map) return;
      const front = productFrontend.find(f => f.product_json_ID === String(map.product_json_ID));
      if (!front) return;
      // const {data}=await axios.get(`http://localhost:8000/product-chatbot/chatbot/productAemDetails?url=${encodeURIComponent(front.PRODUCT_DETAILS_AEM_URL)}`);
      const { data } = await apiClient.post(`/product-chatbot/chatbot/productAemDetails?url=${encodeURIComponent(front.PRODUCT_DETAILS_AEM_URL)}`, { ntID: userNTID });
      setProdDetails({ ...data });
    } catch (e) { console.error(e); }
  };
  const askProd = async q => {
    if (!prodSession) { pushBot("Please pick a product first.", "product"); return ''; }
    try {
      // const {data}=await axios.post('http://localhost:8000/product-chatbot/chatbot/message',{session_id:prodSession,query:q});
      const { data } = await apiClient.post('/biu-chatbot/message', { ntID: userNTID, session_id: prodSession, query: q });
      setCitations(data.citations || []);
    if(data.citations.length == 0){
        return "I don't have appropriate infromations about this query. Please ask a different question or select a different bot."
      }        

      return data.answer;
    } catch (e) { console.error(e); return "Sorry — It's not you, its us. Please try again after sometime."; }
  };
  /* ─── ask wrappers for other bots ───────────────────── */
  const askNri = async q => {
    try {
      // const { data } = await axios.post('http://localhost:8000/nri-chatbot/nri-chatbot/chat/query', {
      const { data } = await apiClient.post('/biu-chatbot/chat/query', {
        query: q,
        category: selNriCategory || 'GST',
        session_id: '',
        response_type: 'default',
        ntID: userNTID,
      });
      setCitations(data.citations || []);
      if(data.citations.length == 0){
        return "I don't have appropriate infromations about this query. Please ask a different question or select a different bot."
      }        
      return data.answer;
    } catch (e) {
      console.error(e);
      return "Sorry — It's not you, its us. Please try again after sometime.";
    }
  };

  const buildLifePayload = (q) => {
    const base = { question: q, ntID: userNTID };
    return lifeSessionRef
      ? { ...base, token: lifeSessionRef }
      : { ...base, token: starterTokenRef };
  };

  const askLife = async q => {
    const body = buildLifePayload(q);
    const { data } = await apiClient.post('/biu-chatbot/life-chatbot/chat2', body);
    console.log("here...", data)
    setCitations(data.citations || []);

    const sid = pickSessionID(data);
    if (sid !== lifeSessionRef) {
      setLifeSessionRef(String(sid));
      setStarterTokenRef(String(sid));
    }
    if(data.citations.length == 0){
      return "I don't have appropriate infromations about this query. Please ask a different question or select a different bot."
    }        

    return data.answer.replace(/\[([^\]]+)\]\([^)]*\)/g,'[$1]');
  };

  const askUw = async q => {
    try {
      const { data } = await apiClient.post('/biu-chatbot/underwriter-chatbot/chat_uw',
        { token: uwToken, question: q, ntID: userNTID }); 
        setUwSession(data.sessionid); 
        setCitations(data.citations || []);
        if(data.citations.length == 0){
          return "I don't have appropriate infromations about this query. Please ask a different question or select a different bot."
        }        
        return data.answer;
    } catch (e) { console.error(e); return "Sorry — It's not you, its us. Please try again after sometime."; }
  };




  /* ─── reset routine (for restart) ───────────────────── */

  const resetSelective = () => {
    setTyping(false);
    setBot(null);
    setProdSession(''); setSelProd(''); setProdDetails(null); setCitations([]);
    setSelNriCategory('');
  }

  const getUsername = async () => {
    try{
    const { data } = await apiClient.post('/biu-chatbot/generic/getuserfullname', { user_id: userNTID, ntID: userNTID });
    console.log(data)
    return data.username
  }catch(e){
    console.log(e)
    // Logout()

  }
  }

  const resetAll = async () => {
    /* clear all state */
    setMessages([]);
    setInput('');
    setTyping(false);
    setBot(null);
    setProdSession(''); setSelProd(''); setProdDetails(null); setCitations([]);
    setSelNriCategory('');
    greeted.current = false;            // allow greeting again
    setResetCount(c => c + 1);            // trigger the greeting effect
    set_product_category_state(0)
    setPosetiveFeedBackIndex(-9999)
    setNegativeFeedBackIndex(-9999)
  };

  /* ─── main send (free-text) ─────────────────────────── */
  const handleSend = async (q = input, show_message = true) => {
    if (!q.trim()) return;
    if (show_message) {
      pushUser(q);
    }
    setInput(''); setTyping(true);
    let ans = '';
    switch (bot) {
      case 'product': ans = await askProd(q); break;
      case 'nri':
        if (!selNriCategory) {
          pushBot("Please select a category first:", 'nri',
            nriCategories.map(cat => ({ text: cat, icon: <PublicIcon /> })));
          setTyping(false);
          return;
        }
        ans = await askNri(q);
        break;
      case 'lifeai': ans = await askLife(q); break;
      case 'uw': ans = await askUw(q); break;
      default:
        pushBot("Please select a service first.", null, [
          { text: "I have questions about products", icon: <SmartToyIcon /> },
          { text: "I have questions about NRIs", icon: <PublicIcon /> },
          { text: "I have a question about Login and Claim process", icon: <GroupsIcon /> },
          { text: "I need underwriting help", icon: <SecurityIcon /> }
        ]);
        setTyping(false); return;
    }
    pushBot(ans, bot); setTyping(false);
  };
  const submitFeedbackNegative = async () => {
    await apiClient.post('/biu-chatbot/generic/feedback', { session_id: bot,ntID:userNTID, query: 'str', response: document.getElementById('feedback_negative_value').value, feedback: 'thumbs_down', product: bot, chat_messages: JSON.stringify(messages) })
    setFeedback_open(0)
  }
  const submitFeedbackPosetive = async (index) => {
    if(index == posetiveFeedBackIndex || index == negativeFeedBackIndex){
      return 
    }
    setPosetiveFeedBackIndex(index)
    await apiClient.post('/biu-chatbot/generic/feedback', { session_id: bot,ntID:userNTID, query: 'str', response: '', feedback: 'thumbs_up', product: bot, chat_messages: JSON.stringify(messages) })
  }
  const openFeedbackNegative = (index) => {
    if(index == posetiveFeedBackIndex || index == negativeFeedBackIndex){
      return 
    }

    setNegativeFeedBackIndex(index)
    setFeedback_open(1)
  }

  /* ─── feedback send (product) ───────────────────────── */
  const prodFeedback = async kind => {
    try {
      const lastUser = [...messages].reverse().find(m => m.role === 'user');
      const lastBot = [...messages].reverse().find(m => m.role === 'assistant' && m.botType === 'product');
      if (!lastUser || !lastBot) return;
      // await axios.post('http://localhost:8000/product-chatbot/chatbot/feedback',{
      await apiClient.post('http://localhost:8000/product-chatbot/chatbot/feedback', {
        session_id: prodSession, query: lastUser.text, response: lastBot.text,
        feedback: kind, product: selProd, ntID: userNTID
      });
      notice('Thanks for the feedback!', 'success');
    } catch (e) { console.error(e); notice('Failed to send feedback', 'error'); }
  };
  /* ─── Dialog body ───────────────────────────────────── */
  const dialogBody = () => {
    if (dialog.type === 'attachments')
      return (
        <>
          <DialogTitle>Product attachments</DialogTitle>
          <DialogContent dividers>
            {prodDetails ?
              ['brochure', 'product_ppt', 'one_pager', 'terms_and_conditions'].map(k =>
                prodDetails[k] && (
                  <ListItem key={k} component="a" href={prodDetails[k]} target="_blank" button>
                    <ListItemIcon><AttachFileIcon /></ListItemIcon>
                    <ListItemText primary={k.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} />
                  </ListItem>
                )
              ) : <Typography>No attachments.</Typography>}
          </DialogContent>
        </>
      );
    if (dialog.type === 'citations')
      return (
        <>
          <DialogTitle>Sources & citations</DialogTitle>
          <DialogContent dividers>
            {citations.map((c, i) => (
              <Typography key={i} variant="body2" sx={{ mb: 2, p: 2, backgroundColor: theme.palette.grey[50], borderRadius: 1 }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{c.trim('#')}</ReactMarkdown>
              </Typography>
            ))}
          </DialogContent>
        </>
      );
    if (dialog.type === 'feedback')
      return (
        <>
          <DialogTitle>Rate my last response</DialogTitle>
          <DialogContent dividers>
            <Box sx={{ display: 'flex', justifyContent: 'space-around', mt: 1 }}>
              <Button variant="outlined" startIcon={<ThumbUpOffAltIcon />}
                onClick={() => { prodFeedback('positive'); setDialog({ open: false }); }}>
                Helpful
              </Button>
              <Button variant="outlined" color="error" startIcon={<ThumbDownIcon />}
                onClick={() => { prodFeedback('negative'); setDialog({ open: false }); }}>
                Not helpful
              </Button>
            </Box>
          </DialogContent>
        </>
      );
    return null;
  };
  /* ─── render ───────────────────────────────────────── */
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline /><GlobalStyles />
      <div style={{ position: 'fixed', height: '220px', width: '220px', 'left': '0px', 'top': '250px', backgroundColor: 'black', color: 'white', zIndex: 9999, fontSize: '8px', display: 'none' }}>

        <table>
          <th><td>Name</td><td>Value</td></th>
          <tr><td>Selected Bot</td><td>{bot}</td></tr>
          <tr><td>lifeSession</td><td>{lifeSession}</td></tr>
          <tr><td>lifeSessionRef</td><td>{lifeSessionRef}</td></tr>
          <tr><td>starterTokenRef</td><td>{starterTokenRef}</td></tr>

        </table>
      </div>
      {/* — AppBar — */}
      <AppBar color="white" position="fixed" sx={{ zIndex: 1, background: '#ffffff' }}>
        <Toolbar>
          <Box
            component="img"
            src={iQuestLogo}
            alt="LIVA"
            sx={{ height: 32, }}
          // sx={{ height: 64 }}
          />

          <Typography variant="h4" sx={{ flexGrow: 1 }}>
          </Typography>
          {/* Restart */}
          <Tooltip title="Restart chat">
            <Typography variant="h12" sx={{ flexGrow: 1 }}>Restart</Typography>
            <IconButton color="inherit" onClick={resetAll}><ReplayIcon /></IconButton>
          </Tooltip>
          <Tooltip title="Notifications">
            <IconButton color="inherit">
              <Badge badgeContent={0} color="secondary"><NotificationsIcon /></Badge>
            </IconButton>
          </Tooltip>
          {bot === 'product' && (
            <>
              {prodDetails && (
                <Tooltip title="Attachments">
                  <IconButton color="inherit" onClick={() => setDialog({ open: true, type: 'attachments' })}>
                    <AttachFileIcon />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
              {citations.length > 0 && (
                <Tooltip title="Citations">
                  <IconButton color="inherit" onClick={() => setDialog({ open: true, type: 'citations' })}>
                    <MenuBookIcon />
                  </IconButton>
                </Tooltip>
              )}
          {/* <Button variant='outlined' onClick={Logout}>Logout</Button> */}

        </Toolbar>
      </AppBar>
      {/* — Drawer — */}
      {/* <Drawer anchor="left" open={drawer} onClose={() => setDrawer(false)}>

        <Box sx={{ width: 280 }}>
          <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
            <SmartToyIcon color="primary" sx={{ mr: 1 }} /><Typography variant="h6">ICICI Prudential</Typography>
          </Box>
          {JSON.stringify(botMeta)}
          <List>
            {Object.keys(botMeta).map(id => (
              <ListItem key={id}>
                <ListItemButton selected={bot === id}
                  onClick={() => { setBot(id); pushBot("How can I help you?", id); setDrawer(false); }}>
                  <ListItemIcon>{botMeta[id].icon}</ListItemIcon>
                  <ListItemText primary={botMeta[id].label}/>
                </ListItemButton>
              </ListItem>
            ))}
            <ListItem>
              <ListItemButton onClick={() => { resetAll(); setDrawer(false); }}>
                <ListItemIcon><ReplayIcon /></ListItemIcon>
                <ListItemText primary="Restart chat" />
                <ListItemText>
                </ListItemText>
              </ListItemButton>
            </ListItem>
          </List>
        </Box>
      </Drawer> */}
      {/* — Chat pane — */}
      <Box sx={{ pt: { xs: 7, sm: 8 }, background: '#fff', pb: bottomPad, height: '100vh', display: 'flex', flexDirection: 'column' }}>
        {/* Main scrollable chat area */}
        <Modal
          open={feedback_open}
          onClose={() => { setFeedback_open(0); setNegativeFeedBackIndex(-9999) }}
        >
          <div style={{ margin: '250px' }}>
            <div style={{ padding: '20px', backgroundColor: 'white', overflowY: 'scroll', height: '250px', width: '100%', marginLeft: '0' }}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                <h4>Please provide your feedback and opinion. </h4>
              </Typography>
              <TextField minRows={3} sx={{ width: '100%', 'marginBottom': '5' }} placeholder='Tell us your feedback' id="feedback_negative_value" />

              <Button variant="contained" color="primary" sx={{ float: 'right', marginTop: '15px' }} onClick={submitFeedbackNegative}>Submit</Button>
              <Button variant="outlined" color="primary" sx={{ float: 'right', marginTop: '15px', marginRight: '15px' }} onClick={FeedbackPopupClose}>Close</Button>


            </div>
          </div>

        </Modal>
        <Box
          sx={{ 
    height: '100vh', // Forces the whole chat window to match screen height
    display: 'flex', 
    flexDirection: 'column' 
  }}>
        <Box sx={{ flexGrow: 1, overflowY: 'auto', maxHeight:'100%', px: { xs: 1, sm: 2 }, pb: 2, pt: 3, mb:10 }}>
          {introLoading ? (<center sx={{ fontSize: '27px', color: 'gray' }}>Initializing session. Please wait ...</center>) : (<></>)}
          {messages.map((m, i) => <Bubble key={i} bot={bot} index={i} m={m} options={m.options} onOptionClick={handleOption} length_vec={messages.length} posetiveFeedBackIndex={posetiveFeedBackIndex}  submitFeedbackPosetive={submitFeedbackPosetive} negativeFeedBackIndex={negativeFeedBackIndex} openFeedbackNegative={openFeedbackNegative}  />)}
          {typing && <Bubble m={{ role: 'assistant', text: '...', botType: bot }} />}
          <div ref={endRef} />
          <br /><br />
        </Box></Box>
        {/* Fixed bottom section with input */}
        <Box sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          bgcolor: 'background.paper',
          borderTop: '1px solid',
          borderColor: 'divider',
          zIndex: 10
        }}>
          <Box sx={{ px: { xs: 1, sm: 2 }, py: 1 }}>
                {bot=='uw' ?(<>

            <div style={{ fontSize: '14px', backgroundColor: '#ffc800', padding: '5px', border: '1px solid #ff6a00', borderRadius: '6px' }}>
              <b>Note on Underwriter bot:</b><br/>
            Answers are based on the latest UW Manual for sales. Please note that the answers may not reflect any interim changes or policy updates done after the manual's last publication date
            While the answers align with the UW manual, all the final decisions remain at the discretion of the underwriter for all cases.
            </div>            <br/>

            </>) : (<></>)}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Type your message..."
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              />
              <IconButton disabled={!input.trim()} onClick={() => handleSend()}>
                <SendIcon sx={{ color: "rgb(112 48 160)" }} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </Box>
      {/* — Dialog — */}
      <Dialog open={dialog.open} onClose={() => setDialog({ open: false })} maxWidth="sm" fullWidth>
        {dialogBody()}
        <DialogActions>
          <Button onClick={() => setDialog({ open: false })} startIcon={<CloseIcon />}>Close</Button>
        </DialogActions>
      </Dialog>
      {/* — Snackbar — */}
      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack({ ...snack, open: false })}>
        <Alert severity={snack.sev} onClose={() => setSnack({ ...snack, open: false })}>{snack.msg}</Alert>
      </Snackbar>
    </ThemeProvider>
  );
}

